import { useCallback } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';

import { type AllergenProperty } from '@sg/graphql-schema';

import { type AllergenTypes } from '../../machines';
import { getProductDietaryRestrictionsNames } from '../../utils';

/**
 * Returns a function that generates the "Contains {restrictions}" label based on provided restrictions.
 */
export const useDietaryRestrictions = (props: Props) => {
  const { restrictions } = props;

  const { formatMessage } = useIntl();

  return useCallback(
    (params: Params) => {
      const { allergenProperties } = params;

      const activeDietaryRestrictions = getProductDietaryRestrictionsNames(
        restrictions,
        allergenProperties,
      );

      if (!activeDietaryRestrictions) return;

      return formatMessage(messages.containsRestrictions, {
        restrictions: activeDietaryRestrictions,
      });
    },
    [restrictions, formatMessage],
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly containsRestrictions: { readonly restrictions: MessageValue };
}>({
  containsRestrictions: {
    defaultMessage: 'Contains {restrictions}',
    description: 'Dietary Restrictions | Contains restrictions',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  restrictions: AllergenTypes;
};

type Params = {
  allergenProperties: readonly AllergenProperty[] | undefined;
};
